/**
 * The members grid's columns. Every cell here is drawn by a plain function
 * the grid calls directly, so none of them may call hooks; anything they need
 * comes in through `MemberColumnsContext`.
 */

import { DropdownMenuItem } from '@roger/ui-components';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Loader2,
  Mail,
  MailX,
  Merge,
  Send,
  UserCheck,
  UserMinus,
} from 'lucide-react';
import {
  BoolCell,
  MenuCell,
  MultiSelectCell,
  OpenCell,
  TextCell,
} from '../../spreadsheet';
import type { UserType } from '../../../services/api/models/UserType';
import type { RoleResponse } from '../../../services/api/types.gen';
import type { RogerDocClinicianAccount } from '../../../services/accountLinkApi';
import { USER_TYPE_OPTIONS } from '../../../constants/userTypes';
import { filterUserVisibleRoles } from '../../../utils/rbacRoles';
import { byMostPermissive, grantCount } from '../../../utils/roleGroups';
import {
  MEMBER_STATUS_BADGE_STYLES,
  MEMBER_STATUS_LABELS,
  MEMBER_STATUS_ORDER,
  MEMBER_STATUS_SHORT_LABELS,
  canEditProfile,
  isAgencyMember,
  memberStatus,
} from '../memberStatus';
import type { OrgMemberResponse } from '../types';
import { type ClinicColumnsContext, clinicColumns } from './clinicColumns';

const TRUE_TEXT = new Set(['true', 'yes', 'y', '1', 'x', '✓']);
const FALSE_TEXT = new Set(['false', 'no', 'n', '0', '']);

/**
 * A member's agency roles for the cell, the one that grants the most first,
 * so "Agency Administrator +2" names the role that matters. In the order
 * held until the catalogue has loaded.
 */
export function rolesMostPermissiveFirst(
  member: OrgMemberResponse,
  roleById: ReadonlyMap<string, RoleResponse> | null
): string[] {
  const held = filterUserVisibleRoles(member.role_assignments ?? []);
  if (!roleById) return held.map(role => role.name);
  return [...held]
    .sort(byMostPermissive(role => grantCount(roleById.get(role.id))))
    .map(role => role.name);
}

/** A member's linked Roger App accounts. */
export function memberAppAccounts(
  ctx: Pick<MemberColumnsContext, 'rogerDocLinksByEmail'>,
  member: OrgMemberResponse
): RogerDocClinicianAccount[] {
  return ctx.rogerDocLinksByEmail[member.email.toLowerCase()] ?? [];
}

/** A member's titles by label, in the member's own order. */
export function titleLabels(member: OrgMemberResponse): string[] {
  return (member.user_types ?? []).map(
    value =>
      USER_TYPE_OPTIONS.find(option => option.value === value)?.label ?? value
  );
}

/**
 * Titles from pasted text: labels or codes, separated by commas, kept in the
 * pasted order. Null when the text names none of them, so a stray paste
 * changes nothing.
 */
export function parseTitles(text: string): UserType[] | null {
  const words = text
    .split(',')
    .map(word => word.trim().toLowerCase())
    .filter(Boolean);
  if (!words.length) return [];
  const picked: UserType[] = [];
  for (const word of words) {
    const option = USER_TYPE_OPTIONS.find(
      o => o.label.toLowerCase() === word || o.value.toLowerCase() === word
    );
    if (option && !picked.includes(option.value)) picked.push(option.value);
  }
  return picked.length ? picked : null;
}

/** "Office Admin +3": the first name, and how many more. */
function firstAndCount(names: readonly string[]): string {
  if (!names.length) return '—';
  return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
}

export type MemberColumnsContext = {
  /** Names, phone and titles: super-user or `clinician-mgr:access`. */
  canEditUserInfo: boolean;
  canEditRogerEmployee: boolean;
  /** The Titles column: EMR agencies only. */
  showTitles: boolean;
  /** Clinics and Employee type: EMR agencies only. */
  showClinics: boolean;
  clinicSetup: ClinicColumnsContext;
  rogerDocLinksByEmail: Record<string, RogerDocClinicianAccount[]>;
  rogerDocAccountsLoaded: boolean;
  rogerDocAccountsFailed: boolean;
  saveField: (
    member: OrgMemberResponse,
    patch: Partial<OrgMemberResponse>
  ) => void;
  saveTitles: (member: OrgMemberResponse, titles: UserType[]) => void;
  /** Agency-scoped e-signature credentials string. */
  saveCredentials: (member: OrgMemberResponse, credentials: string) => void;
  /** The role catalogue by id, null until it has loaded. */
  roleById: ReadonlyMap<string, RoleResponse> | null;
  /** Opens the agency-roles editor; null (read-only) until the catalogue loads. */
  editAgencyRoles: ((member: OrgMemberResponse) => void) | null;
  /** The Licenses column; null without the right to see licenses. */
  licenses: LicensesColumnContext | null;
  /** Opens the member's Roger App accounts. */
  openAppAccounts: (member: OrgMemberResponse) => void;
  currentUserId?: string;
  canInvite: boolean;
  canDeactivate: boolean;
  canCancelInvite: boolean;
  onResendInvite: (member: OrgMemberResponse) => void;
  onRetryDeactivation?: (member: OrgMemberResponse) => void;
  onToggleActivation?: (member: OrgMemberResponse) => void;
  onCancelInvite?: (member: OrgMemberResponse) => void;
  /** Not invited yet: send the first invitation (asks first). */
  onSendInvite: (member: OrgMemberResponse) => void;
  /** Opens the account merge keeping this member; absent without the right. */
  onMergeAccounts?: (keep: OrgMemberResponse) => void;
  /** Whether that row action is still running for that member. */
  isBusy: (action: RowAction, member: OrgMemberResponse) => boolean;
};

export type LicensesColumnContext = {
  /** Counts by user id, members with none left out; null until loaded. */
  counts: Record<string, number> | null;
  /** The counts could not be loaded, so the cell does not claim none. */
  failed: boolean;
  open: (member: OrgMemberResponse) => void;
};

/** Row actions that send a request, so a slow one is not sent twice. */
export type RowAction = 'resend' | 'retry' | 'activation' | 'cancel';

/** The row menu: each action only where its permission and the row allow it. */
function actionsCell(ctx: MemberColumnsContext, member: OrgMemberResponse) {
  const isSelf =
    !!ctx.currentUserId && member.roger_user_id === ctx.currentUserId;
  const isDeactivated = !!member.deactivated_at;
  const status = memberStatus(member);
  // Kinnser staff nobody has invited: no membership here yet, so no
  // invitation to resend or cancel, nothing to deactivate and no settings.
  const notInvited = status === 'not_invited';
  // Offered only to somebody who never arrived; once they have signed in,
  // taking their access away is deactivation's job.
  const canRetractInvite =
    ctx.canCancelInvite &&
    !!ctx.onCancelInvite &&
    !!member.roger_user_id &&
    !isSelf &&
    !!member.is_pending &&
    !notInvited;
  const showResend = ctx.canInvite && !!member.is_pending && !notInvited;
  // Not on your own row: the route refuses it.
  const showRetry =
    ctx.canDeactivate &&
    !!ctx.onRetryDeactivation &&
    !!member.roger_user_id &&
    !isSelf &&
    member.deactivation_status === 'workos_removal_pending';
  // Not alongside Cancel invite: deactivating leaves the invitation live.
  // Reactivate stays, so somebody put on hold before arriving can come off it.
  const showToggle =
    ctx.canDeactivate &&
    !!ctx.onToggleActivation &&
    !!member.roger_user_id &&
    !isSelf &&
    !notInvited &&
    (!canRetractInvite || isDeactivated);
  // Only somebody who signs in here can be the account a merge keeps, and
  // not on your own row: the route refuses a merge naming your own account.
  const showMerge =
    !!ctx.onMergeAccounts &&
    !!member.roger_user_id &&
    !member.unclaimed_import &&
    status === 'active' &&
    !isSelf;
  return (
    <MenuCell label={`Actions for ${member.email}`}>
      {notInvited && ctx.canInvite && (
        // No busy flag: the confirmation stays open, its buttons waiting,
        // until the invitation is sent.
        <DropdownMenuItem onSelect={() => ctx.onSendInvite(member)}>
          <Mail className='mr-2 h-4 w-4' />
          Send invite
        </DropdownMenuItem>
      )}
      {showResend && (
        <DropdownMenuItem
          disabled={ctx.isBusy('resend', member)}
          onSelect={() => ctx.onResendInvite(member)}
        >
          <Send className='mr-2 h-4 w-4' />
          Resend invite
        </DropdownMenuItem>
      )}
      {showRetry && (
        <DropdownMenuItem
          disabled={ctx.isBusy('retry', member)}
          onSelect={() => ctx.onRetryDeactivation?.(member)}
        >
          <UserMinus className='mr-2 h-4 w-4' />
          Retry deactivation
        </DropdownMenuItem>
      )}
      {showToggle && (
        <DropdownMenuItem
          disabled={ctx.isBusy('activation', member)}
          onSelect={() => ctx.onToggleActivation?.(member)}
        >
          {isDeactivated ? (
            <UserCheck className='mr-2 h-4 w-4' />
          ) : (
            <UserMinus className='mr-2 h-4 w-4' />
          )}
          {isDeactivated ? 'Reactivate' : 'Deactivate'}
        </DropdownMenuItem>
      )}
      {showMerge && (
        <DropdownMenuItem onSelect={() => ctx.onMergeAccounts?.(member)}>
          <Merge className='mr-2 h-4 w-4' />
          Merge with another account…
        </DropdownMenuItem>
      )}
      {canRetractInvite && (
        <DropdownMenuItem
          disabled={ctx.isBusy('cancel', member)}
          onSelect={() => ctx.onCancelInvite?.(member)}
        >
          <MailX className='mr-2 h-4 w-4' />
          Cancel invite
        </DropdownMenuItem>
      )}
    </MenuCell>
  );
}

export function memberColumns(
  ctx: MemberColumnsContext
): ColumnDef<OrgMemberResponse>[] {
  const { saveField, saveTitles, saveCredentials } = ctx;
  const canEdit = (m: OrgMemberResponse) =>
    ctx.canEditUserInfo && isAgencyMember(m);
  const canEditPersonal = (m: OrgMemberResponse) =>
    ctx.canEditUserInfo && canEditProfile(m);
  const nameColumn = (
    field: 'first_name' | 'last_name',
    header: string
  ): ColumnDef<OrgMemberResponse> => {
    const patch = (value: string): Partial<OrgMemberResponse> =>
      field === 'first_name' ? { first_name: value } : { last_name: value };
    return {
      id: field,
      header,
      meta: {
        clipboard: {
          copy: m => m[field] ?? '',
          paste: (m, text) => {
            if (text.trim()) saveField(m, patch(text.trim()));
          },
          canPaste: canEditPersonal,
        },
      },
      accessorFn: m => m[field] ?? '',
      size: 110,
      cell: ({ row }) => (
        <TextCell
          value={row.original[field] ?? ''}
          placeholder={header}
          disabled={!canEditPersonal(row.original)}
          onCommit={value => saveField(row.original, patch(value))}
        />
      ),
    };
  };
  return [
    nameColumn('first_name', 'First name'),
    nameColumn('last_name', 'Last name'),
    {
      id: 'email',
      header: 'Email',
      meta: { clipboard: { copy: m => m.email } },
      accessorFn: m => m.email,
      size: 221,
      // How they sign in, so read-only. It still opens, to show a long
      // address in full and copy it.
      cell: ({ row }) => <TextCell value={row.original.email} readOnly />,
    },
    {
      id: 'phone',
      header: 'Phone',
      meta: {
        clipboard: {
          copy: m => m.phone_number ?? '',
          paste: (m, text) => {
            saveField(m, { phone_number: text.trim() || null });
          },
          canPaste: canEditPersonal,
        },
      },
      accessorFn: m => m.phone_number ?? '',
      size: 130,
      enableSorting: false,
      cell: ({ row }) => (
        <TextCell
          value={row.original.phone_number ?? ''}
          placeholder='Phone'
          type='tel'
          disabled={!canEditPersonal(row.original)}
          onCommit={value =>
            saveField(row.original, { phone_number: value || null })
          }
        />
      ),
    },
    ...(ctx.showTitles
      ? [
          {
            id: 'titles',
            header: 'Titles',
            meta: {
              clipboard: {
                copy: m => titleLabels(m).join(', '),
                paste: (m, text) => {
                  const titles = parseTitles(text);
                  if (titles) saveTitles(m, titles);
                },
                canPaste: canEdit,
              },
            },
            enableSorting: false,
            size: 130,
            cell: ({ row }) => {
              const member = row.original;
              return (
                <MultiSelectCell
                  options={USER_TYPE_OPTIONS}
                  value={member.user_types ?? []}
                  // The member's own order, as everywhere else they are listed.
                  text={titleLabels(member).join(', ') || undefined}
                  disabled={!canEdit(member)}
                  placeholder='—'
                  ariaLabel={`Clinical titles for ${member.email}`}
                  onChange={values => {
                    // The cell hands back the options' order; the first title
                    // is the member's primary one, so keep theirs and add new
                    // ones at the end.
                    const held = member.user_types ?? [];
                    const picked = values as UserType[];
                    saveTitles(member, [
                      ...held.filter(title => picked.includes(title)),
                      ...picked.filter(title => !held.includes(title)),
                    ]);
                  }}
                />
              );
            },
          } satisfies ColumnDef<OrgMemberResponse>,
        ]
      : []),
    ...(ctx.showTitles
      ? [
          {
            id: 'credentials',
            header: 'Credentials',
            meta: {
              clipboard: {
                copy: (m: OrgMemberResponse) => m.credentials ?? '',
                paste: (m: OrgMemberResponse, text: string) => {
                  saveCredentials(m, text);
                },
                canPaste: canEdit,
              },
            },
            accessorFn: (m: OrgMemberResponse) => m.credentials ?? '',
            size: 110,
            enableSorting: false,
            cell: ({ row }: { row: { original: OrgMemberResponse } }) => (
              <TextCell
                value={row.original.credentials ?? ''}
                placeholder='e.g. RN'
                disabled={!canEdit(row.original)}
                onCommit={value => {
                  saveCredentials(row.original, value);
                }}
              />
            ),
          } satisfies ColumnDef<OrgMemberResponse>,
        ]
      : []),
    {
      id: 'roger_employee',
      header: 'Roger Employee',
      meta: {
        clipboard: {
          copy: m => (m.roger_employee ? 'TRUE' : 'FALSE'),
          canPaste: m =>
            ctx.canEditRogerEmployee &&
            isAgencyMember(m) &&
            !(m.roger_employee && !m.is_roger_employee),
          paste: (m, text) => {
            const value = text.trim().toLowerCase();
            // Anything that is not clearly yes or no leaves it as it is.
            if (TRUE_TEXT.has(value)) {
              saveField(m, { is_roger_employee: true });
            } else if (FALSE_TEXT.has(value)) {
              saveField(m, { is_roger_employee: false });
            }
          },
        },
      },
      accessorFn: m => (m.roger_employee ? 1 : 0),
      size: 110,
      enableSorting: false,
      cell: ({ row }) => {
        // An internal address makes the person a Roger employee whatever the
        // stored flag says, so the flag can only be cleared on a customer
        // address.
        const byEmail =
          row.original.roger_employee && !row.original.is_roger_employee;
        return (
          <BoolCell
            value={!!row.original.roger_employee}
            disabled={
              !ctx.canEditRogerEmployee ||
              !isAgencyMember(row.original) ||
              byEmail
            }
            label={
              byEmail
                ? `${row.original.email} is a Roger employee by internal email address`
                : `${row.original.email} is a Roger employee`
            }
            onCommit={value =>
              saveField(row.original, { is_roger_employee: value })
            }
          />
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      meta: { clipboard: { copy: m => MEMBER_STATUS_LABELS[memberStatus(m)] } },
      size: 90,
      accessorFn: m => MEMBER_STATUS_ORDER.indexOf(memberStatus(m)),
      cell: ({ row }) => {
        const status = memberStatus(row.original);
        return (
          <div className='flex h-9 items-center justify-center px-2'>
            <span
              title={MEMBER_STATUS_LABELS[status]}
              className={`whitespace-nowrap rounded-full border px-2 py-px text-[11px] font-medium leading-4 ${MEMBER_STATUS_BADGE_STYLES[status]}`}
            >
              {MEMBER_STATUS_SHORT_LABELS[status]}
            </span>
          </div>
        );
      },
    },
    {
      id: 'agency_roles',
      header: 'Agency roles',
      meta: {
        clipboard: {
          copy: m => rolesMostPermissiveFirst(m, ctx.roleById).join(', '),
        },
      },
      enableSorting: false,
      size: 180,
      cell: ({ row }) => {
        const member = row.original;
        const names = rolesMostPermissiveFirst(member, ctx.roleById);
        const edit = ctx.editAgencyRoles;
        return (
          <OpenCell
            text={firstAndCount(names)}
            title={names.join('\n')}
            muted={!names.length}
            disabled={!edit || !isAgencyMember(member)}
            onOpen={() => edit?.(member)}
          />
        );
      },
    },
    ...(ctx.showClinics ? clinicColumns(ctx.clinicSetup) : []),
    {
      id: 'app_accounts',
      header: 'App accounts',
      meta: {
        clipboard: {
          copy: m =>
            memberAppAccounts(ctx, m)
              .map(account => account.username)
              .join(', '),
        },
      },
      enableSorting: false,
      size: 160,
      cell: ({ row }) => {
        // The Roger Docs call behind the accounts is slow, so say so rather
        // than "None" until it has answered, or say it could not.
        if (ctx.rogerDocAccountsFailed && !ctx.rogerDocAccountsLoaded) {
          return (
            // Opening it loads the accounts again in the dialog.
            <OpenCell
              text='Unavailable'
              title='Roger App accounts could not be loaded. Open to try again.'
              muted
              disabled={!isAgencyMember(row.original)}
              onOpen={() => ctx.openAppAccounts(row.original)}
            />
          );
        }
        if (!ctx.rogerDocAccountsLoaded) {
          return (
            <div className='flex h-9 items-center justify-center px-2'>
              <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
            </div>
          );
        }
        const names = memberAppAccounts(ctx, row.original).map(
          account => account.username
        );
        return (
          <OpenCell
            text={names.length ? names.join(', ') : '—'}
            title={names.join('\n')}
            muted={!names.length}
            disabled={!isAgencyMember(row.original)}
            onOpen={() => ctx.openAppAccounts(row.original)}
          />
        );
      },
    },
    ...(ctx.licenses ? [licensesColumn(ctx.licenses)] : []),
    {
      id: 'actions',
      header: 'Actions',
      meta: { headerAlign: 'center' },
      enableSorting: false,
      size: 84,
      cell: ({ row }) => actionsCell(ctx, row.original),
    },
  ];
}

function licensesColumn(
  ctx: LicensesColumnContext
): ColumnDef<OrgMemberResponse> {
  const countOf = (member: OrgMemberResponse) =>
    ctx.counts?.[member.roger_user_id ?? ''] ?? 0;
  const label = (count: number) =>
    count ? `${count} ${count === 1 ? 'license' : 'licenses'}` : '—';
  return {
    id: 'licenses',
    header: 'Licenses',
    meta: { clipboard: { copy: m => label(countOf(m)) } },
    enableSorting: false,
    size: 120,
    cell: ({ row }) => {
      const member = row.original;
      if (!ctx.counts && !ctx.failed) {
        return (
          <div className='flex h-9 items-center justify-center px-2'>
            <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
          </div>
        );
      }
      const count = countOf(member);
      return (
        <OpenCell
          text={ctx.failed ? 'View' : label(count)}
          muted={ctx.failed || !count}
          disabled={!isAgencyMember(member)}
          onOpen={() => ctx.open(member)}
        />
      );
    },
  };
}
